import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import LanguageCard from '@/components/LanguageCard';
import PuzzleMotif from '@/components/PuzzleMotif';
import useSEO from '@/hooks/useSEO';
import AdSlot from '@/components/AdSlot';
import EvolvingRibbon from '@/components/EvolvingRibbon';
import InstallAppButton from '@/components/InstallAppButton';
import { ArrowRight, Shield, Users2, Volume2, Trophy, Leaf, Heart } from 'lucide-react';

/* Home page — calm, low-pressure framing.
   Section order (as requested):
   1. Hero (simpler copy, one clear promise)
   2. How a session feels
   3. One family account
   4. Growing library (languages grid)
   5. Calm features (what's actually in the product today)
   6. Sponsored banner
   7. Closing CTA
*/

const FEATURES = [
    { icon: Leaf, title: 'Learn at your pace', body: 'No streaks, no timers during play, no guilt for a quiet week. Come back whenever it suits you.' },
    { icon: Shield, title: 'Safe for children', body: 'No behavioural ads, no user-to-user messaging, minimal data collected. Built to the ICO Children\u2019s Code.' },
    { icon: Users2, title: 'Built for families', body: 'Parents create the account and child profiles. Siblings see each other\u2019s progress without seeing real names.' },
    { icon: Volume2, title: 'Hear every word', body: 'Tap a word to hear it spoken. Some languages are in beta \u2014 we\u2019re refining the audio with native speakers.' },
];

export default function Home() {
    useSEO({
        title: 'Linguistic Quest — language puzzles for kids: Shona, Yoruba, French, Portuguese & more',
        description: 'Family-friendly puzzle games that teach heritage and world languages to children aged 4–16+. Learn Shona, Ndebele, Yoruba, Swahili, French, Portuguese and Spanish through word search, crossword, balloon pop and four more puzzles. Native-style pronunciation, ad-free paid plans, free to start.',
        path: '/',
        image: '/brand/hero/landing-hero-2026.png',
        jsonLdId: 'home-page',
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Linguistic Quest',
            url: 'https://www.linguisticquest.com/',
            potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.linguisticquest.com/languages?q={search_term_string}',
                'query-input': 'required name=search_term_string',
            },
        },
    });
    const [languages, setLanguages] = useState([]);
    useEffect(() => {
        api.get('/languages').then(({ data }) => setLanguages(data.languages || []));
    }, []);
    const live = languages.filter((l) => l.status === 'live');
    const coming = languages.filter((l) => l.status === 'coming_soon').slice(0, 4);

    return (
        <main data-testid="page-home">
            {/* HERO — globally inclusive, SEO-strong H1 */}
            <section className="relative overflow-hidden grain-bg">
                <div className="hero-orb w-96 h-96 bg-lq-peach-500 -top-20 -right-20" />
                <div className="hero-orb w-80 h-80 bg-lq-purple-300 -bottom-20 -left-10" />
                <div className="absolute top-8 right-8 md:top-16 md:right-20 pointer-events-none">
                    <PuzzleMotif size={200} opacity={0.14} />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-14 md:pt-20 md:pb-20 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="inline-flex items-center gap-2 bg-lq-purple-100 text-lq-purple-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" data-testid="hero-eyebrow">
                            For families &middot; ages 4&ndash;16+
                        </span>
                        <h1 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.03] text-lq-ink-black tracking-tight" data-testid="hero-tagline">
                            Language puzzles for kids.
                        </h1>
                        <p className="mt-5 text-lg text-lq-ink-700 max-w-xl leading-relaxed">
                            A calm family habit, born in Zimbabwe and made for every household. From a grandmother in Harare to a mother in Marseille to a teacher in London.
                        </p>
                        <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 max-w-md text-sm text-lq-ink-900">
                            <li className="flex items-center gap-2"><span aria-hidden>🇿🇼</span> Shona</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇿🇼</span> Ndebele</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇳🇬</span> Yoruba</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇰🇪</span> Swahili</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇫🇷🇸🇳</span> French</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇵🇹🇲🇿</span> Portuguese</li>
                            <li className="flex items-center gap-2"><span aria-hidden>🇪🇸</span> Spanish</li>
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-lq-purple-300/30 transition-colors" data-testid="hero-cta-register">
                                Start free <ArrowRight size={18} />
                            </Link>
                            <Link to="/languages" className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-6 py-3 rounded-full transition-colors" data-testid="hero-cta-languages">
                                Browse languages
                            </Link>
                            <Link to="/support" className="inline-flex items-center gap-2 bg-lq-peach-500 hover:bg-lq-peach-600 text-white font-semibold px-6 py-3 rounded-full transition-colors" data-testid="hero-cta-donate">
                                <Heart size={18} /> Donate
                            </Link>
                            <InstallAppButton />
                        </div>
                        <p className="mt-4 text-xs text-lq-ink-500">No card needed. Free to start. Cancel any time.</p>
                        <div className="mt-5"><EvolvingRibbon /></div>
                    </div>
                    <div className="relative">
                        <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-2xl shadow-lq-purple-300/40 mx-auto max-w-md">
                            <img
                                src="/brand/hero/landing-hero-2026.png"
                                alt="Children from around the world playing Linguistic Quest, greeting each other in Shona (Mhoro), French (Bonjour), Portuguese (Olá), Swahili (Jambo), Zulu (Sawubona), Spanish (Hola) and Yoruba (Bawo ni)"
                                className="w-full h-auto block"
                                data-testid="hero-mascot-image"
                                fetchPriority="high"
                                decoding="async"
                                width="1024"
                                height="1024"
                            />
                        </div>
                        <div className="absolute -bottom-5 -left-2 md:-left-10 bg-white rounded-2xl rounded-bl-sm border-2 border-lq-peach-300 shadow-lg px-5 py-3 max-w-xs font-display text-base text-lq-ink-900" data-testid="hero-image-caption">
                            A calm way to learn together.
                        </div>
                    </div>
                </div>
            </section>

            {/* EUROPE & AFRICA — new expansion section using uploaded artwork */}
            <section className="bg-white border-y border-lq-ink-100 py-14 md:py-20" data-testid="home-europe-africa">
                <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1.1fr,1fr] gap-10 items-center">
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">Africa and Europe, one puzzle at a time</p>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">
                            A world&rsquo;s worth of words.
                        </h2>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed">
                            Linguistic Quest started in Zimbabwe with Shona, the language of our own families, and grew outward through Ndebele, Yoruba, Swahili, French, Portuguese and now Spanish. Spain joins France and Portugal in our European chapter.
                        </p>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed">
                            Every language we add is family-led, native-reviewed, and chosen because someone&rsquo;s grandchild is waiting to learn it. Africa stays at the heart of everything we publish.
                            <Link to="/about" className="ml-1 text-lq-purple-700 hover:underline">Read the founding story &rarr;</Link>
                        </p>
                    </div>
                    <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-lq-purple-300/30">
                        <img
                            src="/brand/hero/europe-puzzles.png"
                            alt="Puzzle pieces arcing across Africa and Europe, with Paris, Lisbon and Barcelona joining the Linguistic Quest catalogue"
                            className="w-full h-auto block"
                            loading="lazy"
                            decoding="async"
                            width="1024"
                            height="1024"
                        />
                    </div>
                </div>
            </section>

            {/* HOW A SESSION FEELS — moved to near top */}
            <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 relative overflow-hidden">
                <div className="absolute right-0 top-20 pointer-events-none"><PuzzleMotif size={160} opacity={0.10} /></div>
                <div className="grid md:grid-cols-[1fr,1.3fr] gap-12 items-center relative">
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">How a session feels</p>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">Gentle. Short. Real.</h2>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed max-w-md">
                            Pick a theme. Find a word. See the meaning. Tap the speaker to hear it. Save it to your word box. That&rsquo;s one word. A session is ten minutes; a week is a hundred words; a year is a language.
                        </p>
                        <p className="mt-4 text-lq-ink-700 max-w-md">
                            No timers chase you. No streaks punish a quiet day. Your pace, your pleasure.
                        </p>
                    </div>
                    <div className="bg-white border-2 border-lq-ink-100 rounded-3xl p-6 shadow-lg">
                        <div className="grid grid-cols-6 gap-1 text-center select-none font-display text-lg" aria-hidden="true">
                            {['M','H','U','R','I','Z','T','R','E','N','D','A','B','A','B','A','L','L','G','O','G','O','K','I','M','W','A','N','A','Y'].map((ch, i) => (
                                <div key={i} className={`aspect-square flex items-center justify-center rounded-md border ${[0,1,2,3,4].includes(i) ? 'cell-found border-lq-peach-500' : 'border-lq-ink-100 bg-lq-paper'}`}>{ch}</div>
                            ))}
                        </div>
                        <div className="mt-4 bg-lq-peach-100 rounded-xl p-4 flex items-center justify-between">
                            <div>
                                <div className="font-display text-xl text-lq-ink-900">mhuri <span className="text-lq-ink-500">&rarr;</span> family</div>
                                <div className="text-xs text-lq-ink-500">Shona &middot; Seedlings &middot; saved to vocabulary</div>
                            </div>
                            <div className="text-lq-peach-600 font-display text-2xl">+10 pts</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ONE FAMILY ACCOUNT — placed directly under "how it feels" */}
            <section className="bg-lq-purple-50 border-y border-lq-ink-100 py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">One family account</p>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">Everyone in the family, one login.</h2>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed max-w-lg">
                            The parent creates the account and adds a child profile for each learner. Just a nickname and age, no legal names. On paid plans, parents can play and earn points too. Nothing leaves your household.
                        </p>
                        <ul className="mt-5 space-y-2 text-sm text-lq-ink-900">
                            <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">&check;</span> Parent controls billing, profiles and language choice</li>
                            <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">&check;</span> Children play in their own profile, no email for them to manage</li>
                            <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">&check;</span> Family leaderboard on paid plans, a shared game, not a race</li>
                        </ul>
                        <p className="mt-3 text-xs text-lq-ink-500">Free plan: 1 child profile, parent administers. Paid plans: parent plays too. Explorer&nbsp;3 &middot; Polyglot&nbsp;4 &middot; Global&nbsp;5 child profiles. See <Link to="/pricing" className="text-lq-purple-700 hover:underline">Pricing</Link>.</p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-5 py-2.5 rounded-full" data-testid="family-cta-register">
                                Create a family account
                            </Link>
                            <Link to="/leaderboard" className="inline-flex items-center gap-2 text-lq-purple-700 hover:text-lq-purple-900 font-semibold px-5 py-2.5 rounded-full">
                                <Trophy size={16}/> See the leaderboard
                            </Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { name: 'Mum', kind: 'Parent', pts: 420 },
                            { name: 'Tapiwa', kind: 'Age 8', pts: 310 },
                            { name: 'Ruvarashe', kind: 'Age 6', pts: 175 },
                            { name: 'Dad', kind: 'Parent', pts: 90 },
                        ].map((p, i) => (
                            <div key={i} className="bg-white rounded-2xl border border-lq-ink-100 p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-lq-peach-100 inline-flex items-center justify-center text-lq-peach-600 font-display font-bold">{p.name.charAt(0)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold text-lq-ink-900 truncate">{p.name}</div>
                                    <div className="text-xs text-lq-ink-500">{p.kind}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-semibold text-lq-ink-900">{p.pts}</div>
                                    <div className="text-[10px] text-lq-ink-500">pts</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LANGUAGES — growing library */}
            <section className="max-w-7xl mx-auto px-6 py-16 md:py-20">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">A growing library</p>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">Pick a language to begin.</h2>
                        <p className="mt-2 text-lq-ink-500 max-w-xl">A growing catalogue of heritage and world languages, native-reviewed and family-led.</p>
                    </div>
                    <Link to="/languages" className="text-lq-purple-700 font-semibold hover:underline" data-testid="home-all-languages">
                        All languages &rarr;
                    </Link>
                </div>
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="home-languages-grid">
                    {live.map((l) => <LanguageCard key={l.id} language={l} wordCount={l.word_count} />)}
                </div>
                {coming.length > 0 && (
                    <div className="mt-6">
                        <p className="text-xs uppercase tracking-wider text-lq-ink-500 mb-3">Rolling out next</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="home-coming-soon">
                            {coming.map((l) => <LanguageCard key={l.id} language={l} />)}
                        </div>
                    </div>
                )}
            </section>

            {/* CALM FEATURES */}
            <section className="bg-lq-paper border-y border-lq-ink-100 py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-6">
                    {FEATURES.map((f) => (
                        <div key={f.title} className="bg-white p-6 rounded-2xl border border-lq-ink-100" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,'-')}`}>
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-purple-100 text-lq-purple-700">
                                <f.icon size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-xl font-semibold text-lq-ink-900">{f.title}</h3>
                            <p className="mt-2 text-sm text-lq-ink-500 leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SUPPORT STRIP */}
            <section className="max-w-7xl mx-auto px-6 pt-4" aria-label="Support us">
                <div className="rounded-2xl border border-lq-peach-300 bg-lq-peach-100/60 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="home-support-strip">
                    <p className="text-sm text-lq-ink-900">
                        <strong className="font-display">Love what we&rsquo;re building?</strong> Most of Linguistic Quest is free for families &mdash; if it made yours smile, you can chip in from &pound;1 to keep the puzzles running.
                    </p>
                    <Link to="/support" className="shrink-0 inline-flex items-center gap-2 bg-lq-peach-500 hover:bg-lq-peach-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="home-support-cta">&hearts; Support us</Link>
                </div>
            </section>
            {/* SPONSORED BANNER — moved down, presented calmly */}
            <section className="max-w-7xl mx-auto px-6 py-12" aria-label="Sponsored">
                <p className="text-xs uppercase tracking-wider text-lq-ink-500 mb-3">Sponsored. Family-safe brands.</p>
                <AdSlot slug="home-hero" />
            </section>

            {/* PER-LANGUAGE SEO LINKS — internal-link hub for parents searching specific languages */}
            <section className="bg-white border-y border-lq-ink-100 py-14 md:py-16" data-testid="home-language-landings">
                <div className="max-w-6xl mx-auto px-6">
                    <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">Find your language</p>
                    <h2 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">A short guide to each language we teach.</h2>
                    <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <Link to="/learn-shona-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-shona">
                            <div className="font-display text-base text-lq-ink-900">Learn Shona for kids <span className="text-xs text-lq-ink-500 align-middle">🇿🇼</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Zimbabwe&rsquo;s heritage language</div>
                        </Link>
                        <Link to="/learn-ndebele-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-ndebele">
                            <div className="font-display text-base text-lq-ink-900">Learn Ndebele for kids <span className="text-xs text-lq-ink-500 align-middle">🇿🇼</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Clicks, songs and stories from Matabeleland</div>
                        </Link>
                        <Link to="/learn-yoruba-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-yoruba">
                            <div className="font-display text-base text-lq-ink-900">Learn Yoruba for kids <span className="text-xs text-lq-ink-500 align-middle">🇳🇬</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Tones, proverbs and music from West Africa</div>
                        </Link>
                        <Link to="/learn-swahili-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-swahili">
                            <div className="font-display text-base text-lq-ink-900">Learn Swahili for kids <span className="text-xs text-lq-ink-500 align-middle">🇰🇪</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">East Africa&rsquo;s lingua franca</div>
                        </Link>
                        <Link to="/learn-french-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-french">
                            <div className="font-display text-base text-lq-ink-900">Learn French for kids <span className="text-xs text-lq-ink-500 align-middle">🇫🇷🇸🇳</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Spoken from Paris to Dakar, Abidjan and Montreal</div>
                        </Link>
                        <Link to="/learn-portuguese-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors" data-testid="home-landing-portuguese">
                            <div className="font-display text-base text-lq-ink-900">Learn Portuguese for kids <span className="text-xs text-lq-ink-500 align-middle">🇵🇹🇲🇿</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Lusophone words from Lisbon to Maputo and Luanda</div>
                        </Link>
                        <Link to="/learn-spanish-for-kids" className="bg-lq-paper hover:bg-lq-purple-50 border border-lq-ink-100 hover:border-lq-purple-300 rounded-2xl px-5 py-4 transition-colors relative" data-testid="home-landing-spanish">
                            <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider text-lq-peach-600 bg-lq-peach-100 border border-lq-peach-300 rounded-full px-2 py-0.5">Coming soon</span>
                            <div className="font-display text-base text-lq-ink-900">Learn Spanish for kids <span className="text-xs text-lq-ink-500 align-middle">🇪🇸</span></div>
                            <div className="mt-0.5 text-xs text-lq-ink-500">Castilian and Latin American Spanish</div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="max-w-4xl mx-auto px-6 py-16 md:py-20 text-center relative overflow-hidden">
                <div className="absolute inset-x-0 -top-8 flex justify-center pointer-events-none"><PuzzleMotif size={180} opacity={0.10} /></div>
                <h2 className="relative font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Your heritage, one calm puzzle at a time.</h2>
                <p className="relative mt-4 text-lq-ink-700 max-w-2xl mx-auto">
                    Start free. Upgrade whenever it feels right. Cancel any time, no questions asked.
                </p>
                <div className="relative mt-8 flex flex-wrap justify-center gap-3">
                    <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full transition-colors" data-testid="home-bottom-cta">
                        Start free
                    </Link>
                    <Link to="/pricing" className="inline-flex items-center gap-2 text-lq-purple-700 hover:text-lq-purple-900 font-semibold px-6 py-3 rounded-full">
                        See plans
                    </Link>
                </div>
            </section>
        </main>
    );
}
